import React, { useMemo, useState } from 'react';
import { Btn, DataTable, KPI, KPIRow, Loading, Msg, Section, SubTabs, useAdminGet } from '../kit';
import { Check, DateRange, inputCls } from '../email/emailKit';
import { asStr, first, fmt, istDay, num, pct, rowsOf, secs } from './shared';

/**
 * Admin → Onboarding → Funnel: from the modal being shown to a paid plan.
 *
 *   Shown → Industry → Keyword → Explore / Skip step 2 / Skip step 1 / Abandoned
 *         → Landed (3 picks served) → Opened a pick → Hit a lock (search / filter
 *         / contact) → Block click (trial / upgrade) → Trial started → Paid
 *
 * Window: the person's first onboarding "shown" in [from, to] (India days).
 * Server-side (onboarding_admin.funnel): onboarding_events + onboarding_progress
 * for the steps and their timings, site_events for what happened on Cosmos,
 * billing_subscriptions for trials (by trial_form origin) and paid plans. Team
 * and internal accounts are left out unless "Include team" is ticked. Time per
 * step comes from the client-measured ms and the server's *_at columns, never
 * from site_events.at (a beacon's arrival time).
 *
 * The QA tile counts plans sheets that opened within 60 s of finishing
 * onboarding with no locked click before them — it should stay at 0.
 */

/** `of`: the stage a row's conversion is read against (the bar is always
 *  against "Saw the modal"). Branches (explore / skips / lock kinds) read
 *  against their parent; Cosmos rows against "Landed". */
const STAGES: { key: string; label: string; alts?: string[]; indent?: boolean; of?: string }[] = [
  { key: 'shown', label: 'Saw the modal' },
  { key: 'industry', label: 'Picked an industry', alts: ['industry_picked'], of: 'shown' },
  { key: 'keyword', label: 'Picked a keyword', alts: ['keyword_picked'], of: 'industry' },
  { key: 'explored', label: 'Explore Cosmos (industry + keyword)', alts: ['explore', 'industry_keyword'], indent: true },
  { key: 'skip2', label: 'Skipped step 2 (industry only)', alts: ['skip_step2', 'industry_only'], indent: true },
  { key: 'skip1', label: 'Skipped step 1', alts: ['skip_step1', 'skipped'], indent: true },
  { key: 'abandoned', label: 'Abandoned (no finish after 30 min)', indent: true },
  { key: 'landed', label: 'Landed on their 3 picks', of: 'shown' },
  { key: 'opened', label: 'Opened a pick', alts: ['opened_pick', 'card_open'], of: 'landed' },
  { key: 'lock_any', label: 'Hit a lock', alts: ['locked', 'lock'], of: 'landed' },
  { key: 'lock_search', label: 'Search lock', indent: true },
  { key: 'lock_filter', label: 'Filter lock', indent: true },
  { key: 'lock_contact', label: 'Contact University lock', indent: true },
  { key: 'block_click', label: 'Clicked the upgrade block', alts: ['block'], of: 'landed' },
  { key: 'block_trial', label: 'Start free trial', indent: true, alts: ['block_click_trial'] },
  { key: 'block_upgrade', label: 'Upgrade to Voyager', indent: true, alts: ['block_click_upgrade'] },
  { key: 'trial', label: 'Started a trial', alts: ['trials', 'trial_started'], of: 'landed' },
  { key: 'paid_7d', label: 'Paid within 7 days', alts: ['paid7'], of: 'landed' },
  { key: 'paid_30d', label: 'Paid within 30 days', alts: ['paid30'], of: 'landed' },
  { key: 'paid', label: 'Paid (ever)', alts: ['paid_ever'], of: 'landed' },
];

/** Columns a table opens with; the rest stay under the Columns picker. */
const MAIN_COLS = ['shown', 'industry', 'keyword', 'explored', 'skip2', 'skip1', 'abandoned', 'landed', 'opened',
  'lock_any', 'block_click', 'trial', 'paid'];
const RATE_COLS = ['explore_rate', 'trial_rate', 'paid_rate'];
const splitCols = (rows: any[], lead: string[]): { columns: string[]; optional: string[] } => {
  const keys = rows[0] ? Object.keys(rows[0]).filter(k => !k.startsWith('_')) : [];
  const want = [...lead, ...MAIN_COLS, ...RATE_COLS].filter((k, i, a) => a.indexOf(k) === i && keys.includes(k));
  return { columns: want, optional: keys.filter(k => !want.includes(k)) };
};

const BREAKDOWNS: { key: string; label: string }[] = [
  { key: 'industry', label: 'Industry' },
  { key: 'industry_keyword', label: 'Industry × keyword' },
  { key: 'chip_index', label: 'Chip position' },
  { key: 'outcome', label: 'Outcome' },
  { key: 'entry', label: 'Entry' },
  { key: 'picks_mode', label: 'Picks mode' },
  { key: 'trial_origin', label: 'Trial origin' },
];

/** The stage counts, whatever the payload calls them. */
const stageCounts = (d: any): Record<string, number | null> => {
  const out: Record<string, number | null> = {};
  if (!d) return out;
  const flat: Record<string, any> = { ...(d.counts || {}), ...(d.totals || {}), ...d };
  rowsOf(d, 'stages').forEach((s: any) => { if (s?.key) flat[s.key] = first(s.count, s.n, s.users, s.value); });
  STAGES.forEach(s => {
    out[s.key] = num(first(flat[s.key], ...(s.alts || []).map(a => flat[a])));
  });
  return out;
};

const timingRows = (d: any): Record<string, any>[] => {
  const t = d && first(d.timing, d.timings, d.time);
  if (Array.isArray(t)) return t;
  const src = t && typeof t === 'object' ? t : d || {};
  const row = (label: string, k: string) => ({
    step: label,
    median: secs(first(src[`${k}_p50`], src[`${k}_median`], src[k]?.p50, src[k]?.median)),
    p75: secs(first(src[`${k}_p75`], src[k]?.p75)),
  });
  return [row('Step 1 — open → industry', 's_step1'), row('Step 2 — industry → keyword', 's_step2'),
    row('Open → finish', 's_total')];
};

const SHORT: Record<string, string> = { shown: 'shown', industry: 'industry', landed: 'landed' };

const Bars: React.FC<{ counts: Record<string, number | null> }> = ({ counts }) => {
  const top = counts.shown || 0;
  return (
    <div className="flex flex-col gap-1">
      {STAGES.map(s => {
        const v = counts[s.key];
        if (v === null || v === undefined) return null;
        const base = s.of ? counts[s.of] : null;
        return (
          <div key={s.key} className="flex items-center gap-2 text-sm">
            <div className={`w-64 shrink-0 truncate ${s.indent ? 'pl-4 text-grayscale-1100' : 'text-grayscale-1300'}`} title={s.label}>
              {s.label}
            </div>
            <div className="flex-1 bg-grayscale-200 rounded-full h-3 overflow-hidden min-w-[4rem]">
              <div className={`h-3 rounded-full ${s.indent ? 'bg-primary-300' : 'bg-primary-500'}`}
                style={{ width: `${top ? Math.min(100, (v / top) * 100) : 0}%` }} />
            </div>
            <div className="w-44 text-right text-grayscale-1300 font-medium whitespace-nowrap">
              {fmt(v)}
              <span className="text-grayscale-1100 text-xs"> {pct(v, top)}
                {s.of && s.of !== 'shown' && base ? ` · ${pct(v, base)} of ${SHORT[s.of] || s.of}` : ''}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/** Industry × keyword: one row per industry, its chips as cells shaded by picks. */
const Heatmap: React.FC<{ rows: any[]; metric: 'picked' | 'explore_rate' }> = ({ rows, metric }) => {
  const byInd = new Map<string, any[]>();
  rows.forEach(r => {
    const ind = asStr(r.industry);
    if (!ind) return;
    if (!byInd.has(ind)) byInd.set(ind, []);
    byInd.get(ind)!.push(r);
  });
  const picked = (r: any) => num(first(r.keyword_picked, r.picked, r.users, r.count)) || 0;
  const explored = (r: any) => num(first(r.explored, r.explore)) || 0;
  const val = (r: any) => (metric === 'picked' ? picked(r) : (picked(r) ? explored(r) / picked(r) : 0));
  const max = Math.max(1e-9, ...rows.map(val));
  if (!byInd.size) return <div className="text-sm text-grayscale-1100">No keyword picks in this window.</div>;
  return (
    <div className="flex flex-col gap-1 overflow-x-auto">
      {Array.from(byInd.entries()).map(([ind, list]) => (
        <div key={ind} className="flex items-stretch gap-1 min-w-[48rem]">
          <div className="w-56 shrink-0 text-xs text-grayscale-1300 truncate py-1.5" title={ind}>{ind}</div>
          {list.filter(r => asStr(r.keyword)).sort((a, b) => (num(a.chip_index) ?? 99) - (num(b.chip_index) ?? 99) || picked(b) - picked(a))
            .map(r => {
              const v = val(r);
              const alpha = 0.08 + 0.85 * (v / max);
              return (
                <div key={asStr(r.keyword)} className="flex-1 min-w-[7rem] rounded px-1.5 py-1 text-[11px] leading-tight"
                  style={{ background: `rgba(175, 142, 94, ${alpha})`, color: alpha > 0.6 ? '#fff' : '#141414' }}
                  title={`${ind} · ${asStr(r.keyword)} — picked ${picked(r)}, explored ${explored(r)}`}>
                  <div className="truncate">{asStr(r.keyword)}</div>
                  <div className="font-bold">{metric === 'picked' ? fmt(picked(r)) : pct(explored(r), picked(r))}</div>
                </div>
              );
            })}
        </div>
      ))}
    </div>
  );
};

const Funnel: React.FC = () => {
  const [from, setFrom] = useState(istDay(29));
  const [to, setTo] = useState(istDay(0));
  const [team, setTeam] = useState(false);
  const [origin, setOrigin] = useState('');
  const [flow, setFlow] = useState('');
  const [version, setVersion] = useState('');
  const [by, setBy] = useState('industry');
  const [heat, setHeat] = useState<'picked' | 'explore_rate'>('picked');

  const params = useMemo(() => {
    const p: Record<string, string> = {};
    if (from) p.from = from;
    if (to) p.to = to;
    p.team = team ? '1' : '0';
    if (origin) p.origin = origin;
    if (flow) p.flow = flow;
    if (version.trim()) p.config_version = version.trim();
    return p;
  }, [from, to, team, origin, flow, version]);
  const key = JSON.stringify(params);

  const funnel = useAdminGet<any>('onboarding', 'funnel', params, [key]);
  const trend = useAdminGet<any>('onboarding', 'trend', params, [key]);
  const breakdown = useAdminGet<any>('onboarding', 'breakdown', { ...params, by }, [key, by]);

  const counts = stageCounts(funnel.data);
  const qa = num(first(funnel.data?.qa_modal_on_entry, funnel.data?.qa?.modal_on_entry, funnel.data?.qa_entry_modal));
  const preTracking = num(first(funnel.data?.pre_tracking, funnel.data?.pretracking));
  const trendRows = rowsOf(trend.data, 'days', 'trend');
  const maxShown = Math.max(1, ...trendRows.map(r => num(first(r.shown, r.n)) || 0));
  const bRows = rowsOf(breakdown.data, 'breakdown');

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-4">
        <DateRange label="First shown (India days)" from={from} to={to} onChange={(f, t) => { setFrom(f); setTo(t); }} />
        <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
          <span>Account origin</span>
          <select value={origin} onChange={e => setOrigin(e.target.value)}
            className="h-9 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm">
            <option value="">Any</option>
            <option value="self">Signed up themselves</option>
            <option value="admin">Added by an admin</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
          <span>Flow</span>
          <select value={flow} onChange={e => setFlow(e.target.value)}
            className="h-9 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm">
            <option value="">Both</option>
            <option value="v2">v2 (new)</option>
            <option value="v1">v1 (legacy)</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-grayscale-1100 w-32">
          <span>Config version</span>
          <input value={version} onChange={e => setVersion(e.target.value)} placeholder="any" className={inputCls} />
        </label>
        <div className="pb-2"><Check checked={team} onChange={setTeam}>Include team</Check></div>
        <Btn small onClick={() => { funnel.reload(); trend.reload(); breakdown.reload(); }}>Refresh</Btn>
      </div>

      {funnel.error && <Msg kind="error">Could not load the funnel: {funnel.error}</Msg>}
      {funnel.loading && !funnel.data ? <Loading /> : funnel.data && (
        <>
          <KPIRow>
            <KPI label="Saw the modal" value={fmt(counts.shown)} />
            <KPI label="Finished" value={pct((counts.explored || 0) + (counts.skip2 || 0) + (counts.skip1 || 0), counts.shown)}
              hint="explore or skip" />
            <KPI label="Explore (industry + keyword)" value={pct(counts.explored, counts.shown)} />
            <KPI label="Trial" value={fmt(counts.trial)} hint={pct(counts.trial, counts.shown)} />
            <KPI label="Paid" value={fmt(counts.paid)} hint={pct(counts.paid, counts.shown)} />
            {qa !== null && (
              <KPI label="QA: plans sheet on entry" value={fmt(qa)}
                hint={qa === 0 ? 'as intended' : 'should be 0 — check the sign-in prompt / entry modal'} />
            )}
          </KPIRow>
          {preTracking ? (
            <div className="text-[11px] text-grayscale-1100">
              {fmt(preTracking)} people finished onboarding before step tracking began; they are counted from their progress row.
            </div>
          ) : null}
          <Section title="Funnel">
            <Bars counts={counts} />
          </Section>
          <Section title="Time per step">
            <DataTable rows={timingRows(funnel.data)} columns={['step', 'median', 'p75']} labels={{ p75: '75th percentile' }} />
          </Section>
        </>
      )}

      <Section title="Daily (India time)">
        {trend.error && <Msg kind="error">{trend.error}</Msg>}
        {trend.loading && !trend.data ? <Loading /> : (
          <>
            {trendRows.length > 0 && (
              <div className="flex items-end gap-[2px] h-24 border-b border-grayscale-300" aria-hidden="true">
                {trendRows.map((r, i) => {
                  const s = num(first(r.shown, r.n)) || 0;
                  const e = num(first(r.explored, r.explore)) || 0;
                  return (
                    <div key={i} className="flex-1 flex flex-col justify-end min-w-[3px]"
                      title={`${asStr(first(r.day, r.date))}: shown ${s}, explored ${e}`}>
                      <div className="bg-primary-300 rounded-t-sm" style={{ height: `${((s - Math.min(e, s)) / maxShown) * 96}px` }} />
                      <div className="bg-primary-600" style={{ height: `${(Math.min(e, s) / maxShown) * 96}px` }} />
                    </div>
                  );
                })}
              </div>
            )}
            {(() => {
              const c = splitCols(trendRows, ['day']);
              return (
                <DataTable rows={trendRows} columns={c.columns.length ? c.columns : undefined}
                  optionalColumns={c.optional} tableId="onboarding_trend"
                  empty="Nothing in this window." csvName="onboarding_trend" maxHeight="18rem" defaultSort="-day" />
              );
            })()}
          </>
        )}
      </Section>

      <Section title="Breakdown"
        right={by === 'industry_keyword' ? (
          <span className="flex items-center gap-2 text-xs">
            <SubTabs tabs={['Picked', 'Explore rate']} active={heat === 'picked' ? 'Picked' : 'Explore rate'}
              onChange={t => setHeat(t === 'Picked' ? 'picked' : 'explore_rate')} />
          </span>
        ) : undefined}>
        <SubTabs tabs={BREAKDOWNS.map(b => b.label)} active={BREAKDOWNS.find(b => b.key === by)?.label || ''}
          onChange={t => setBy(BREAKDOWNS.find(b => b.label === t)?.key || 'industry')} />
        {breakdown.error && <Msg kind="error">{breakdown.error}</Msg>}
        {breakdown.loading && !breakdown.data ? <Loading /> : (
          <>
            {by === 'industry_keyword' && <Heatmap rows={bRows} metric={heat} />}
            {(() => {
              const lead = by === 'industry_keyword' ? ['industry', 'keyword', 'chip_index', 'picked']
                : by === 'picks_mode' ? ['picks_mode', 'fallback_used'] : [by];
              const c = splitCols(bRows, lead);
              return (
                <DataTable key={by} rows={bRows} columns={c.columns.length ? c.columns : undefined}
                  optionalColumns={c.optional} tableId={`onboarding_by_${by}`}
                  labels={{ chip_index: 'Chip (0 = first)', explore_rate: 'Explore %', trial_rate: 'Trial %', paid_rate: 'Paid %' }}
                  empty="Nothing in this window." csvName={`onboarding_by_${by}`} maxHeight="24rem" searchable />
              );
            })()}
          </>
        )}
      </Section>
    </div>
  );
};

export default Funnel;
